import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import jexcel, { JExcelElement } from "jexcel";

import "../../../../node_modules/jexcel/dist/jexcel.css";
import {
  ITeacher,
  Subject,
  IClass,
  ISubClass,
  TeacherSubject,
  TeacherSubjectForBulkRegister,
} from "../../../state";
import { csvEscape, parseCsv } from "../../../libs/csvUtils";
import { ModalContext } from "../../../libs/ModalProvider";
import Confirm from "../../../molecules/Confirm";

import styles from "./BulkTeacherSubjectForm.module.scss";

export interface BulkTeacherSubjectFormProps {
  title: string;
  teachers: ITeacher[];
  subjects: Subject[];
  classes: IClass[];
  subClasses: ISubClass[];
  teacherSubjects: TeacherSubject[];
  error: string;
  calling: boolean;
  save: (teacherSubjects: TeacherSubjectForBulkRegister[]) => void;
}

const Fields = {
  email: 0,
  subject: 1,
  klasses: 2,
  check: 3,
};

// jexcel は複数選択ドロップダウンの値を「;」区切りのキー文字列で保持する
const KLASS_VALUE_SEPARATOR = ";";
// CSV 内で担当クラスを区切る文字（列区切りの「,」と衝突するのでセルはクォートされる）
const CSV_KLASS_SEPARATOR = ",";

const CSV_HEADERS = [
  "先生メールアドレス ※必須",
  "教科名 ※必須",
  "担当クラス（カンマ区切り）",
];

type ResolvedRow = {
  error: string;
  teacherId?: number;
  subjectId?: number;
  klassIds: number[];
};

const parseKlassIds = (raw: any): number[] => {
  const parts = Array.isArray(raw)
    ? raw
    : String(raw ?? "").split(KLASS_VALUE_SEPARATOR);
  return parts
    .map(x => Number(String(x).trim()))
    .filter(n => !isNaN(n) && n > 0);
};

// full_name は is_display_full_name=false だと学年が消えるため、同名クラスを学年で判別できるよう学年付きで表記する
const klassLabel = (c: IClass): string => `${c.grade}年${c.name}`;

export const BulkTeacherSubjectForm = (props: BulkTeacherSubjectFormProps) => {
  const ctx = React.useContext(ModalContext);
  const jexcelRef = useRef<HTMLDivElement>(null);
  const CSVInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState({
    sheet: null as null | JExcelElement,
    csvFile: null as null | File,
  });
  const save = props.save;

  const teacherEmailById = useMemo(() => {
    const map = new Map<number, string>();
    props.teachers.forEach(t => map.set(t.id, t.email));
    return map;
  }, [props.teachers]);

  const subjectSource = useMemo(
    () => props.subjects.map(s => ({ id: s.id, name: s.name })),
    [props.subjects],
  );
  const classSource = useMemo(
    () => [
      ...props.classes.map(c => ({ id: c.id, name: klassLabel(c) })),
      ...props.subClasses.map(sc => ({ id: sc.id, name: sc.name })),
    ],
    [props.classes, props.subClasses],
  );
  const teacherSource = useMemo(
    () =>
      props.teachers.map(t => ({
        id: t.id,
        name: `${t.email}（${t.last_name} ${t.first_name}）`,
      })),
    [props.teachers],
  );

  // 既存担当を「1行 = 先生 × 教科 × 担当クラス」にフラット化（教科/クラスは id を保持）
  const baselineData = useMemo(() => {
    const rows: (string | number)[][] = [];
    props.teacherSubjects.forEach(ts => {
      ts.subjects.forEach(s => {
        const klassValue = s.klasses
          .map(k => k.klass_id)
          .join(KLASS_VALUE_SEPARATOR);
        rows.push([ts.teacher_id, s.subject_id, klassValue, ""]);
      });
    });
    return rows;
  }, [props.teacherSubjects]);

  const baselineMap = useMemo(() => {
    const map = new Map<string, string>();
    props.teacherSubjects.forEach(ts => {
      ts.subjects.forEach(s => {
        const key = `${ts.teacher_id}:${s.subject_id}`;
        const ids = s.klasses
          .map(k => k.klass_id)
          .sort((a, b) => a - b)
          .join(",");
        map.set(key, ids);
      });
    });
    return map;
  }, [props.teacherSubjects]);

  const resolveRow = useCallback(
    (teacherRaw: any, subjectRaw: any, klassRaw: any): ResolvedRow => {
      const teacherIdStr = String(teacherRaw ?? "").trim();
      const subjectIdStr = String(subjectRaw ?? "").trim();
      const klassIds = parseKlassIds(klassRaw);
      if (!teacherIdStr && !subjectIdStr && klassIds.length === 0) {
        return { error: "", klassIds: [] };
      }
      if (!teacherIdStr) {
        return { error: "先生を選択してください", klassIds: [] };
      }
      const teacher = props.teachers.find(t => t.id === Number(teacherIdStr));
      if (!teacher) {
        return { error: "先生が見つかりません", klassIds: [] };
      }
      if (!subjectIdStr) {
        return { error: "教科を選択してください", klassIds: [] };
      }
      const subjectId = Number(subjectIdStr);
      const subject = props.subjects.find(s => s.id === subjectId);
      if (!subject) {
        return { error: "教科が見つかりません", klassIds: [] };
      }
      for (const id of klassIds) {
        if (!classSource.find(o => o.id === id)) {
          return { error: "担当クラスが見つかりません", klassIds: [] };
        }
      }
      return {
        error: "",
        teacherId: teacher.id,
        subjectId: subject.id,
        klassIds,
      };
    },
    [props.teachers, props.subjects, classSource],
  );

  const validateSheetRow = useCallback(
    (sheet: JExcelElement, y: number) => {
      const rec = sheet.getRowData(y);
      if (!rec) {
        return;
      }
      const res = resolveRow(
        rec[Fields.email],
        rec[Fields.subject],
        rec[Fields.klasses],
      );
      sheet.setValueFromCoords(Fields.check, y, res.error, false);
    },
    [resolveRow],
  );

  const validateAllRows = useCallback(
    (sheet: JExcelElement) => {
      const rowCount = sheet.getData(false, true).length;
      for (let y = 0; y < rowCount; y++) {
        validateSheetRow(sheet, y);
      }
    },
    [validateSheetRow],
  );

  const showErrorMessage = useCallback(
    (message: string) => {
      if (CSVInputRef.current) {
        CSVInputRef.current.value = "";
      }
      ctx.actions.showModal(
        <Confirm
          content={message}
          closeModal={ctx.actions.closeModal}
          callback={ctx.actions.closeModal}
        />,
      );
    },
    [ctx.actions],
  );

  const exportCSV = useCallback(() => {
    const dataRows: string[][] = [
      ["例：yamada@example.com", "例：数学", "例：1年A,1年B"],
    ];
    props.teacherSubjects.forEach(ts => {
      const email = teacherEmailById.get(ts.teacher_id) ?? "";
      ts.subjects.forEach(s => {
        dataRows.push([
          email,
          s.subject_name,
          s.klasses
            .map(k => {
              const o = classSource.find(opt => opt.id === k.klass_id);
              return o ? o.name : k.klass_name;
            })
            .join(CSV_KLASS_SEPARATOR),
        ]);
      });
    });
    // 参照用（教科一覧・クラス一覧）は右側の列に出力する。取込時はA〜C列のみ読むため無視される
    const subjectRef = [
      "参照用教科一覧（取込時は無視されます）",
      ...props.subjects.map(s => s.name),
    ];
    const classRef = [
      "参照用クラス一覧（取込時は無視されます）",
      ...classSource.map(o => o.name),
    ];
    const totalRows = Math.max(
      dataRows.length + 1,
      subjectRef.length,
      classRef.length,
    );
    const lineList: string[] = [];
    for (let r = 0; r < totalRows; r++) {
      const left = r === 0 ? CSV_HEADERS : dataRows[r - 1] || [];
      lineList.push(
        [
          left[0] ?? "",
          left[1] ?? "",
          left[2] ?? "",
          "",
          "",
          "",
          subjectRef[r] ?? "",
          classRef[r] ?? "",
        ]
          .map(csvEscape)
          .join(","),
      );
    }
    const lines = lineList.join("\n");
    const BOM = String.fromCharCode(0xfeff);
    const blob = new Blob([BOM + lines + "\n"], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "teacher_subject_list.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [props.teacherSubjects, props.subjects, classSource, teacherEmailById]);

  const handleImportCSV = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files ? event.target.files[0] : null;
    if (!file || !state.sheet) {
      return;
    }
    const sheet = state.sheet;
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = e => {
      try {
        const rows = parseCsv((e.target?.result as string).trim());
        if (rows.length === 0) {
          showErrorMessage("無効なCSV形式です");
          return;
        }
        const headerRow = rows[0];
        const isValidHeader =
          headerRow.length >= CSV_HEADERS.length &&
          CSV_HEADERS.every((col, index) => col === headerRow[index]);
        if (!isValidHeader) {
          showErrorMessage(
            "無効な列形式です。適切な列を含む CSV を追加してください",
          );
          return;
        }
        const dataRows = rows
          .slice(1)
          .filter(
            row =>
              !/^(例|#|＃)/.test(row[Fields.email] || "") &&
              [
                row[Fields.email],
                row[Fields.subject],
                row[Fields.klasses],
              ].some(cell => cell),
          );
        if (dataRows.length === 0) {
          showErrorMessage("取り込めるデータがありません。");
          return;
        }

        const importedRows: (string | number)[][] = [];
        for (let i = 0; i < dataRows.length; i++) {
          const row = dataRows[i];
          const lineNo = i + 1;
          const email = (row[Fields.email] || "").trim();
          const subjectName = (row[Fields.subject] || "").trim();
          const klassNames = (row[Fields.klasses] || "")
            .split(/[,，]/)
            .map(n => n.trim())
            .filter(n => n);

          if (!email) {
            showErrorMessage(
              `${lineNo}行目の先生メールアドレスが入力されていません。`,
            );
            return;
          }
          const teacher = props.teachers.find(t => t.email === email);
          if (!teacher) {
            showErrorMessage(
              `${lineNo}行目の先生「${email}」が見つかりません。`,
            );
            return;
          }
          if (!subjectName) {
            showErrorMessage(`${lineNo}行目の教科名が入力されていません。`);
            return;
          }
          const matchedSubjects = props.subjects.filter(
            s => s.name === subjectName,
          );
          if (matchedSubjects.length === 0) {
            showErrorMessage(
              `${lineNo}行目の教科「${subjectName}」が見つかりません。`,
            );
            return;
          }
          if (matchedSubjects.length > 1) {
            showErrorMessage(
              `${lineNo}行目の教科「${subjectName}」が複数あります。教科管理で重複を解消してください。`,
            );
            return;
          }
          const klassIds: number[] = [];
          for (const klassName of klassNames) {
            const klass = classSource.find(o => o.name === klassName);
            if (!klass) {
              showErrorMessage(
                `${lineNo}行目のクラス「${klassName}」が見つかりません。`,
              );
              return;
            }
            klassIds.push(klass.id);
          }
          importedRows.push([
            teacher.id,
            matchedSubjects[0].id,
            klassIds.join(KLASS_VALUE_SEPARATOR),
            "",
          ]);
        }

        const existing = sheet
          .getData(false, true)
          .filter(row => row.some(cell => `${cell ?? ""}`.trim()));
        sheet.setData([...existing, ...importedRows] as any);
        validateAllRows(sheet);
        setState(prev => ({ ...prev, csvFile: file }));
      } catch (error) {
        showErrorMessage("何か問題が発生しました。もう一度試してください。");
      }
    };
    reader.onerror = () => {
      showErrorMessage("何か問題が発生しました。もう一度試してください。");
    };
  };

  useEffect(() => {
    const jexcelRefCurrent = jexcelRef.current;
    if (jexcelRefCurrent) {
      jexcelRefCurrent.innerHTML = "";
    }
    let sheet = null as null | JExcelElement;
    const options = {
      data: baselineData.length ? baselineData : [["", "", "", ""]],
      minSpareRows: 1,
      tableOverflow: true,
      tableHeight: "600px",
      tableWidth: "100%",
      columns: [
        {
          type: "dropdown",
          title: "先生メールアドレス*",
          width: 280,
          source: teacherSource,
          autocomplete: true,
        },
        {
          type: "dropdown",
          title: "教科*",
          width: 160,
          source: subjectSource,
          autocomplete: true,
        },
        {
          type: "dropdown",
          title: "担当クラス",
          width: 320,
          source: classSource,
          multiple: true,
          autocomplete: true,
        },
        { type: "text", title: "チェック結果", width: 260 },
      ],
      onchange: (
        _instance: HTMLElement,
        _cell: HTMLTableCellElement,
        x: number,
        y: number,
        _value: any,
      ) => {
        if (!sheet || Number(x) === Fields.check) {
          return;
        }
        validateSheetRow(sheet, Number(y));
      },
    };
    sheet = jexcel(jexcelRefCurrent as any, options as any);
    setState(s => ({ ...s, sheet }));
  }, [
    baselineData,
    teacherSource,
    subjectSource,
    classSource,
    validateSheetRow,
  ]);

  const addRow = useCallback(() => {
    state.sheet?.insertRow();
  }, [state.sheet]);

  const resetFunc = useCallback(() => {
    if (!state.sheet) {
      return;
    }
    if (CSVInputRef.current) {
      CSVInputRef.current.value = "";
    }
    state.sheet.setData(
      (baselineData.length ? baselineData : [["", "", "", ""]]) as any,
    );
    validateAllRows(state.sheet);
    setState(prev => ({ ...prev, csvFile: null }));
  }, [state.sheet, baselineData, validateAllRows]);

  const buildPayload = useCallback((): TeacherSubjectForBulkRegister[] => {
    if (!state.sheet) {
      return [];
    }
    const merged = new Map<
      string,
      { teacher_id: number; subject_id: number; klassIds: Set<number> }
    >();
    state.sheet.getData(false, true).forEach(row => {
      const res = resolveRow(
        row[Fields.email],
        row[Fields.subject],
        row[Fields.klasses],
      );
      if (res.error || res.teacherId == null || res.subjectId == null) {
        return;
      }
      const key = `${res.teacherId}:${res.subjectId}`;
      const entry = merged.get(key) ?? {
        teacher_id: res.teacherId,
        subject_id: res.subjectId,
        klassIds: new Set<number>(),
      };
      res.klassIds.forEach(id => entry.klassIds.add(id));
      merged.set(key, entry);
    });

    const payload: TeacherSubjectForBulkRegister[] = [];
    merged.forEach((entry, key) => {
      const ids = Array.from(entry.klassIds).sort((a, b) => a - b);
      const baseline = baselineMap.get(key);
      const isNew = baseline === undefined;
      const isChanged = !isNew && baseline !== ids.join(",");
      if ((isNew && ids.length > 0) || isChanged) {
        payload.push({
          teacher_id: entry.teacher_id,
          subject_id: entry.subject_id,
          klass_ids: ids,
        });
      }
    });
    return payload;
  }, [state.sheet, resolveRow, baselineMap]);

  const handleSaveClick = useCallback(() => {
    const payload = buildPayload();
    if (payload.length === 0) {
      showErrorMessage("保存できる変更がありません。");
      return;
    }
    ctx.actions.showModal(
      <Confirm
        calling={props.calling}
        content={
          <>
            <p className="mb-0">
              教科担当の変更内容を保存します、よろしいですか？
            </p>
            <p>担当クラスを空にして保存した教科は、その担当が外れます。</p>
          </>
        }
        closeModal={() => ctx.actions.closeModal()}
        callback={() => save(payload)}
      />,
    );
  }, [buildPayload, showErrorMessage, ctx.actions, props.calling, save]);

  return (
    <div className={styles.wrapper}>
      {props.error && (
        <div className={styles.errorBillboard}>{props.error}</div>
      )}
      <div className={styles.header}>
        <h4>{props.title}</h4>
        <p className={styles.note}>
          教科の登録と編集を一括で行えます。
          <br />
          担当クラスは複数選択できます。先生はメールアドレスで指定します。
          <br />
          ※この機能は追加・更新のみ行います。行を削除しても担当は消えません。
          <br />
          担当を外したい場合は担当クラスを空にして保存するか、各先生の編集画面で削除してください。
        </p>
      </div>
      <div className={styles.actionButtons}>
        <button
          className="btn btn-outline-danger me-3"
          disabled={props.calling}
          onClick={() => {
            ctx.actions.showModal(
              <Confirm
                calling={props.calling}
                content={"編集内容をリセットして元の状態に戻しますか？"}
                closeModal={() => ctx.actions.closeModal()}
                callback={resetFunc}
              />,
            );
          }}
        >
          リセット
        </button>
        <button
          className={`btn btn-outline me-3 ${styles.utilBtn}`}
          type="button"
          onClick={() =>
            ctx.actions.showModal(
              <Confirm
                content={
                  <span>
                    CSVファイルをエクスポートします。
                    <br />
                    <br />
                    既存の担当に加え、選択できる教科・クラスの一覧が右側の参照列に書き出されます（取込時は無視されます）。新しく担当を追加する際は、データ列の最下行から追記してください（担当クラスは「学年＋クラス名」（例:
                    1年A）をカンマ区切り）。2行目は記入例となりますので消さないでください。
                  </span>
                }
                closeModal={() => ctx.actions.closeModal()}
                callback={exportCSV}
              />,
            )
          }
        >
          {props.teacherSubjects.length === 0
            ? "インポート用CSVをダウンロード"
            : "CSVエクスポート"}
        </button>
        <button
          type="button"
          className={`btn btn-outline-primary ${styles.importCsv}`}
          onClick={() => CSVInputRef.current?.click()}
        >
          CSVインポート
          <input
            ref={CSVInputRef}
            type="file"
            accept=".csv"
            onChange={handleImportCSV}
          />
        </button>
      </div>
      {state.csvFile && <p className={styles.fileName}>{state.csvFile.name}</p>}
      <div className={styles.excel} ref={jexcelRef} />
      <p className={styles.note}>
        ※「チェック結果」にエラーが表示されている行は保存されません。エラーを修正してから保存してください。
      </p>
      <div className={styles.action}>
        <button className="btn btn-primary" onClick={addRow}>
          追加
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSaveClick}
          disabled={props.calling}
        >
          保存
        </button>
      </div>
    </div>
  );
};

export default BulkTeacherSubjectForm;
